
import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

interface AuthContextType {
    session: Session | null
    user: User | null
    loading: boolean
    signInWithEmail: (email: string, password: string) => Promise<void>
    signUpWithEmail: (email: string, password: string, fullName: string) => Promise<void>
    signInWithGoogle: () => Promise<void>
    signOut: () => Promise<void>
    getAccessToken: () => string | null
}


const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [session, setSession] = useState<Session | null>(null)
    const [user, setUser] = useState<User | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        // Check active sessions and sets the user
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session)
            setUser(session?.user ?? null)
            setLoading(false)
        })

        // Listen for changes on auth state (logged in, signed out, etc.)
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session)
            setUser(session?.user ?? null)
            setLoading(false)
        })

        return () => subscription.unsubscribe()
    }, [])



    const signInWithEmail = async (email: string, password: string) => {
        const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
        })

        if (error) {
            console.error('Error signing in with email:', error.message)
            throw error
        }
    }

    const signUpWithEmail = async (email: string, password: string, fullName: string) => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    full_name: fullName,
                    name: fullName,
                },
            },
        })

        if (error) {
            console.error('Error signing up with email:', error.message)
            throw error
        }

        // If email confirmation is disabled, user will be auto-signed in
        // If email confirmation is enabled but user already exists, data.user will be null
        if (data.user && !data.session) {
            // Email confirmation is required - inform the user
            throw new Error('Please check your email to confirm your account before signing in.')
        }

        // If we have a session, the user is signed in
        if (data.session) {
            setSession(data.session)
            setUser(data.user)
        }
    }

    const signInWithGoogle = async () => {
        // Check if there's a redirect parameter in the URL (e.g., for accepting invites)
        const urlParams = new URLSearchParams(window.location.search)
        const redirectPath = urlParams.get('redirect')

        // If there's a redirect, use it; otherwise redirect to /app and let routing logic decide
        const redirectTo = redirectPath
            ? `${window.location.origin}${redirectPath}`
            : `${window.location.origin}/app`

        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo,
            },
        })

        if (error) {
            console.error('Error signing in with Google:', error.message)
            throw error
        }
    }

    const signOut = async () => {
        try {
            // Clear any local storage items related to workspace
            localStorage.removeItem('reflow_current_workspace')
            
            const { error } = await supabase.auth.signOut()
            if (error) {
                console.error('Error signing out:', error.message)
                throw error
            }
            
            // Force redirect to landing page
            window.location.href = '/'
        } catch (error) {
            console.error('Error during sign out:', error)
            // Even if there's an error, try to redirect
            window.location.href = '/'
        }
    }

    const getAccessToken = useCallback(() => {
        return session?.access_token ?? null
    }, [session])

    const value = {
        session,
        user,
        loading,
        signInWithEmail,
        signUpWithEmail,
        signInWithGoogle,
        signOut,
        getAccessToken,
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
